import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { PageHeader, rupee, fmtMin } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { istNow, ymd } from "../lib/dates";

function monthNow() {
  return ymd(istNow()).slice(0, 7);
}

export default function Salary() {
  const { branchParam } = useBranch();
  const [month, setMonth] = useState(monthNow());
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [ded, setDed] = useState({ amount: 0, reason: "" });

  const load = () => api.get(`/salary-sheet/${month}`, { params: { branch_id: branchParam } })
    .then((r) => setRows(r.data));
  useEffect(() => { load(); }, [month, branchParam]);

  const saveDeduction = async () => {
    try {
      await api.put(`/salary/${detail.staff_id}/${month}/deductions`, {
        staff_id: detail.staff_id, month, deductions: Number(ded.amount), reason: ded.reason,
      });
      toast.success("Deduction saved"); setDetail(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const approveSalary = async (staff_id) => {
    try {
      await api.put(`/salary/${staff_id}/${month}/approve`);
      toast.success("Salary approved for payroll"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const sendWhatsAppPayslip = async (staff_id) => {
    try {
      const { data } = await api.get(`/salary/${staff_id}/${month}/whatsapp-payslip`);
      if (data.wa_link) { window.open(data.wa_link, "_blank"); toast.success("Opening WhatsApp with the payslip…"); }
      else toast.error("No WhatsApp number on file for this staff member");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const downloadPayslip = async (staff_id) => {
    try {
      const res = await api.get(`/salary/${staff_id}/${month}/payslip`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      link.download = `payslip_${month}.pdf`; link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error("Could not generate payslip"); }
  };

  return (
    <div>
      <PageHeader title="Minute-Based Salary" subtitle="Salary from approved working minutes — appointment time is never used"
        actions={<Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          data-testid="salary-month" className="bg-secondary w-44" />} />

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Employee</TableHead>
              <TableHead className="text-right">Std Min</TableHead>
              <TableHead className="text-right">Biometric</TableHead>
              <TableHead className="text-right">Approved Reg</TableHead>
              <TableHead className="text-right">Extra</TableHead>
              <TableHead className="text-right">Worked</TableHead>
              <TableHead className="text-right">% Std</TableHead>
              <TableHead className="text-right">₹/min</TableHead>
              <TableHead className="text-right">Regular</TableHead>
              <TableHead className="text-right">Extra ₹</TableHead>
              <TableHead className="text-right">Incentives</TableHead>
              <TableHead className="text-right">Advances</TableHead>
              <TableHead className="text-right">Deductions</TableHead>
              <TableHead className="text-right">Final</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.staff_id} className="border-border" data-testid={`salary-row-${r.staff_id}`}>
                <TableCell className="font-medium">
                  {r.staff_name}
                  {r.outstanding_advance > 0 && (
                    <Badge variant="outline" className="ml-2 bg-amber-900/40 text-amber-300 border-amber-800 text-[10px]" data-testid={`salary-outstanding-${r.staff_id}`}>
                      Outstanding {rupee(r.outstanding_advance)}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">{r.standard_monthly_minutes}</TableCell>
                <TableCell className="text-right">{r.total_biometric_minutes}</TableCell>
                <TableCell className="text-right text-primary">{r.approved_regular_minutes}</TableCell>
                <TableCell className="text-right">{r.approved_extra_minutes}</TableCell>
                <TableCell className="text-right font-medium" data-testid={`salary-worked-${r.staff_id}`}>{r.worked_minutes}</TableCell>
                <TableCell className={`text-right ${r.completion_pct >= 100 ? "text-green-400" : r.completion_pct >= 85 ? "text-amber-400" : "text-destructive"}`} data-testid={`salary-completion-${r.staff_id}`}>{r.completion_pct}%</TableCell>
                <TableCell className="text-right">₹{r.per_minute_rate}</TableCell>
                <TableCell className="text-right">{rupee(r.regular_salary)}</TableCell>
                <TableCell className="text-right">{rupee(r.extra_amount)}</TableCell>
                <TableCell className="text-right">{rupee(r.incentives)}</TableCell>
                <TableCell className="text-right text-amber-400" data-testid={`salary-advance-${r.staff_id}`}>{r.advance_deduction ? rupee(r.advance_deduction) : "—"}</TableCell>
                <TableCell className="text-right text-destructive">{rupee(r.deductions)}</TableCell>
                <TableCell className="text-right font-display text-lg text-primary">{rupee(r.final_salary)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-8 text-xs"
                      data-testid={`salary-payslip-${r.staff_id}`}
                      onClick={() => downloadPayslip(r.staff_id)}>Payslip</Button>
                    <Button size="sm" variant="ghost" className="h-8 text-xs"
                      data-testid={`salary-deduct-${r.staff_id}`}
                      onClick={() => { setDetail(r); setDed({ amount: r.deductions, reason: "" }); }}>Adjust</Button>
                    {r.approved_status === "approved"
                      ? <>
                          <Badge variant="outline" className="bg-green-900/40 text-green-300 border-green-800">Payroll</Badge>
                          <Button size="sm" variant="ghost" className="h-8 text-xs text-green-400"
                            data-testid={`salary-wa-payslip-${r.staff_id}`}
                            onClick={() => sendWhatsAppPayslip(r.staff_id)}>WhatsApp</Button>
                        </>
                      : <Button size="sm" className="h-8 text-xs rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"
                          data-testid={`salary-approve-${r.staff_id}`}
                          onClick={() => approveSalary(r.staff_id)}>Approve</Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={15} className="text-center text-muted-foreground py-10">No staff / salary data.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle className="font-display text-2xl">Salary Adjustment — {detail?.staff_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 bg-secondary p-3 rounded-sm">
                <span className="text-muted-foreground">Regular Salary</span><span className="text-right">{rupee(detail.regular_salary)}</span>
                <span className="text-muted-foreground">Overtime / Extra</span><span className="text-right">{rupee(detail.extra_amount)}</span>
                <span className="text-muted-foreground">Regular Incentive (service)</span><span className="text-right" data-testid="detail-regular-incentive">{rupee(detail.regular_incentive ?? detail.incentives)}</span>
                <span className="text-muted-foreground">Product Sale Incentive</span><span className="text-right" data-testid="detail-product-incentive">{rupee(detail.product_incentive || 0)}</span>
                <span className="text-muted-foreground">High-Value Bill Bonus</span><span className="text-right" data-testid="detail-high-value">{rupee(detail.high_value_bonus || 0)}</span>
                <span className="text-muted-foreground">Monthly Target Bonus</span><span className="text-right" data-testid="detail-monthly-target">{rupee(detail.monthly_target_bonus || 0)}</span>
                <span className="text-muted-foreground font-medium">Total Incentive</span><span className="text-right font-medium" data-testid="detail-total-incentive">{rupee((detail.incentives || 0) + (detail.high_value_bonus || 0) + (detail.monthly_target_bonus || 0))}</span>
                <span className="text-muted-foreground">Advance Deducted (this month)</span><span className="text-right text-amber-400">{rupee(detail.advance_deduction || 0)}</span>
              </div>
              {detail.advance_history?.length > 0 && (
                <div data-testid="advance-history">
                  <Label>Advance History</Label>
                  <div className="mt-1.5 border border-border rounded-sm divide-y divide-border max-h-44 overflow-y-auto">
                    {detail.advance_history.map((a) => (
                      <div key={a.id} className="flex items-center justify-between px-3 py-2 text-xs">
                        <span className="text-muted-foreground">{a.date}</span>
                        <span>{rupee(a.amount)}</span>
                        {a.status === "deducted"
                          ? <Badge variant="outline" className="bg-green-900/40 text-green-300 border-green-800">Deducted · {a.applied_month}</Badge>
                          : <Badge variant="outline" className="bg-amber-900/40 text-amber-300 border-amber-800">Pending · {a.applied_month}</Badge>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label>Deductions (₹)</Label>
                <Input type="number" data-testid="deduction-amount" value={ded.amount}
                  onChange={(e) => setDed({ ...ded, amount: e.target.value })} className="mt-1.5 bg-secondary" />
              </div>
              <div>
                <Label>Reason (audit)</Label>
                <Input data-testid="deduction-reason" value={ded.reason}
                  onChange={(e) => setDed({ ...ded, reason: e.target.value })} className="mt-1.5 bg-secondary" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveDeduction} data-testid="deduction-save"
              className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
